import classNames from "classnames";
import { BaseEmoji } from "emoji-mart/dist-es/utils/emoji-index/nimble-emoji-index";
import { observer } from "mobx-react";
import React, { useEffect, useState, Suspense, lazy } from "react";
import { FileThumbnail } from "@fidget/common/components/files/fileThumbnail";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { IAttachment } from "@fidget/common/models/chat/chatItems";
import { IFileUploadInfo } from "@fidget/common/models/fileUpload";
import { isValidText } from "@fidget/common/utils/isValidText";
import styles from "./chatInputComponent.css";
import { useForeContext, useStore } from "../models";
import {
  NotificationLevel,
  NotificationType
} from "@fidget/common/models/mst/ui/ChatNotificationStore";
import { HighlighterSpan } from "@fidget/common/components/text/highlighterSpan/highlighterSpan";
import "regenerator-runtime/runtime"; // required by react-speech-recognition
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { staticConfig } from "@fidget/common/config/static-config";
import { CenterPreloader } from "./centerPreloader/centerPreloader";
export interface Props {
  onMessageSent: (message: string, attachments?: ArrayLike<IAttachment>) => void;
  onTyping: (preview?: string) => void;
  isTypingPreviewEnabled: boolean;
}

// Reduce fore size
const AsyncEmojiPicker = lazy(() => import("@fidget/common/components/emojiPicker"));

export const ChatInputComponent = observer((props: Props) => {
  const { uiStore, profanityStore, fileStore, contextStore } = useStore();
  const context = useForeContext();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [foundProfanity, setFoundProfanity] = useState<string>();
  const [filesInRow, setFilesInRow] = useState<number>(0);
  const [filesContainer, setFilesContainer] = useState<HTMLDivElement>();
  const [textArea, setTextArea] = useState<HTMLTextAreaElement>();

  const { transcript, listening: isMicrophoneActive, resetTranscript } = useSpeechRecognition();

  useEffect(() => {
    setText(transcript);
  }, [transcript]);

  const isMicrophoneEnabled =
    context.state.shared.orgConfig?.webSpeechApiEnabled &&
    SpeechRecognition.browserSupportsSpeechRecognition;

  const messageMaxAllowedCharacters =
    context.state.shared.orgConfig?.messageMaxAllowedCharactersForClient ??
    staticConfig.maxMessageAllowedCharactersForClient;

  const compareTextToLimit = (textToCompare: string) => {
    if (textToCompare.length >= messageMaxAllowedCharacters) {
      uiStore.setChatNotification({
        message: intl.formatMessage("conversation.warning.inputLength.title", {
          limit: messageMaxAllowedCharacters
        }),
        type: NotificationType.ChatMessageLength,
        level: NotificationLevel.Warning,
        onCancel: () => textArea?.focus(),
        onAction: sendMessage,
        actionText: intl.formatMessage("conversation.warning.inputLength.action")
      });
    } else {
      uiStore.removeChatNotificationIfType(NotificationType.ChatMessageLength);
    }
    return textToCompare.substring(0, messageMaxAllowedCharacters);
  };

  useEffect(() => {
    if (filesInRow < 1 && filesContainer) {
      countElementsInRow(filesContainer.clientWidth);
    }
  });

  const sendMessage = () => {
    SpeechRecognition.abortListening();
    if (isValidText(text) || (fileStore.hasFiles && fileStore.isUploadCompleted)) {
      if (context.state.shared.orgConfig?.profanitiesCheckingEnabled) {
        const maybeProfanity = profanityStore.matchProfanity(text);
        if (maybeProfanity) {
          uiStore.setChatNotification({
            message: intl.formatMessage("conversation.error.profanity.title"),
            type: NotificationType.ChatProfanity
          });
          setFoundProfanity(maybeProfanity);
          return;
        }
      }

      const attachments = fileStore.attachments;
      const trimmedText = text.trim();
      props.onMessageSent(trimmedText, attachments);
      setText("");
      fileStore.clear();
    }
    if (textArea) {
      textArea.focus();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.nativeEvent.keyCode === 13 &&
      !event.nativeEvent.shiftKey &&
      !event.nativeEvent.ctrlKey
    ) {
      const isMessageValid = fileStore.hasFiles ? fileStore.isUploadCompleted : isValidText(text);
      if (isMessageValid) {
        event.preventDefault();
        sendMessage();
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const limitedText = compareTextToLimit(event.target.value);
    setText(limitedText);

    if (foundProfanity && !profanityStore.isProfanityIncludedInText(foundProfanity, limitedText)) {
      setFoundProfanity(undefined);
      uiStore.removeChatNotificationIfType(NotificationType.ChatProfanity);
    }
  };

  const countElementsInRow = (containerClientWidth: number) => {
    const elementsInRow = (containerClientWidth - 8) / 84;
    setFilesInRow(Math.floor(elementsInRow));
  };

  const handleEmojiIconClick = () => {
    setIsEmojiPickerOpen(!isEmojiPickerOpen);
  };

  const handleMicrophoneIconClick = () => {
    if (!isMicrophoneActive) {
      resetTranscript();
      SpeechRecognition.startListening({
        language: navigator.language.substr(0, 2),
        continuous: true
      });
    } else {
      SpeechRecognition.stopListening();
      SpeechRecognition.abortListening();
    }
  };

  const addEmoji = (emoji: BaseEmoji) => {
    const textWithEmoji = text + emoji.native;
    const canAddEmoji = compareTextToLimit(textWithEmoji) === textWithEmoji;

    if (canAddEmoji) {
      setText(textWithEmoji);
      props.onTyping(props.isTypingPreviewEnabled ? textWithEmoji : undefined);
    }

    setIsEmojiPickerOpen(false);

    if (textArea) {
      textArea.focus();
    }
  };

  const onFilesAdded = (files: FileList, roomId: string) => {
    const filesArray = Array.from(files);
    for (let i = 0; i < filesArray.length; i++) {
      if (fileStore.fileUploads.length < 12) {
        fileStore.createFileUpload(filesArray[i]!, roomId);
      } else {
        uiStore.setChatNotification({
          message: intl.formatMessage("widget.addFiles.error.max12")
        });
        break;
      }
    }
    if (textArea) {
      textArea.focus();
    }
  };

  const onFileRemoved = (fileInfo: IFileUploadInfo) => {
    fileStore.removeFileUpload(fileInfo);
  };

  const renderFilesCounter = () => (
    <div className={styles.counter}>{fileStore.fileUploads.length}</div>
  );

  const renderSendButton = () => {
    const canSendMessage =
      !fileStore.hasFiles || (fileStore.hasFiles && fileStore.isUploadCompleted);

    return (
      <button
        className={classNames(styles["icon-button"], styles["send-arrow"])}
        disabled={!canSendMessage}
        onClick={(event) => {
          event.preventDefault();
          sendMessage();
        }}
      >
        <i className="icon-send-message" />
        {fileStore.hasFiles && renderFilesCounter()}
      </button>
    );
  };

  const renderEmojiButton = () => {
    return (
      <button
        type="button"
        className={classNames(styles["emoji-button"], styles["icon-button"], {
          [styles.active]: isEmojiPickerOpen
        })}
        onClick={handleEmojiIconClick}
      >
        <i className={"icon-emoji"} />
      </button>
    );
  };

  const renderMicrophoneButton = () => {
    return (
      <button
        type="button"
        className={classNames(styles["mic-button"], styles["icon-button"], {
          [styles.active]: isMicrophoneActive
        })}
        onClick={handleMicrophoneIconClick}
      >
        <i className={"icon-mic"} />
      </button>
    );
  };

  const renderAddFileIcon = () => {
    const renderInput = () => (
      <div className={classNames(styles["icon-button"], styles["icon-button-upload"])}>
        <input
          type={"file"}
          multiple
          title={""}
          onChange={(e) => e.target.files && onFilesAdded(e.target.files, contextStore.foreContext.state.profile?.roomId!)}
        />
        <i className={"icon-attach-widget"} />
      </div>
    );

    return (
      <TooltipComponent
        text={intl.formatMessage("widget.addFiles.tooltip")}
        style={"widget"}
        positionPreset={"top"}
        maxWidth={"148px"}
        width={"148px"}
        changeTransformTooltip={"translateX(-20%)"}
        changeTransformOutset={"translateX(-400%)"}
      >
        {renderInput()}
      </TooltipComponent>
    );
  };

  const renderTextArea = () => {
    return (
      <div className={styles["input-wrap"]}>
        <div className={styles["textarea-container"]}>
          <div className={classNames(styles["ghost-input"])}>
            <HighlighterSpan variant="alert" text={text || " "} highlightWord={foundProfanity} />
            <textarea
              disabled={context.state.room.chat.shouldBlockInput}
              rows={1}
              placeholder={intl.formatMessage("widget.typeYourMessage")}
              onKeyPress={handleKeyPress}
              onChange={handleChange}
              onInput={(event: React.FormEvent<HTMLTextAreaElement>) => {
                props.onTyping(
                  props.isTypingPreviewEnabled
                    ? (event.target as HTMLTextAreaElement).value
                    : undefined
                );
              }}
              id={"widget_chat_input"}
              value={text}
              maxLength={messageMaxAllowedCharacters}
              ref={(el) => setTextArea(el || undefined)}
              autoFocus
            />
          </div>
        </div>
      </div>
    );
  };

  const renderFiles = () => {
    const moreThanThreeFilesRow =
      fileStore.hasFiles && fileStore.fileUploads.length > filesInRow * 3;

    return (
      <div
        className={classNames(styles["files-previews-container"], {
          [styles.scroll]: fileStore.hasFiles && fileStore.fileUploads.length > filesInRow * 3
        })}
        ref={(el) => setFilesContainer(el || undefined)}
      >
        {fileStore.hasFiles &&
          fileStore.fileUploads.map((fileInfo, i) => (
            <FileThumbnail
              fileUploadInfo={fileInfo}
              key={fileInfo.file.name}
              small
              widgetStyle
              isInFirstRow={moreThanThreeFilesRow && i < filesInRow}
              onRetry={(fileUpload) => fileStore.startFileUpload(fileUpload)}
              onRemove={(fileUpload) => onFileRemoved(fileUpload)}
            />
          ))}
      </div>
    );
  };

  const renderEmojiPicker = () => (
    <Suspense fallback={<CenterPreloader />}>
      <AsyncEmojiPicker onEmojiPicked={addEmoji} />
    </Suspense>
  );

  const render = () => {
    const areAttachmentsEnabled =
      !context.state.tagGroup || context.state.tagGroup.attachmentsEnabled;
    const hideInput = context.state.room.chat.shouldBlockInput || context.state.room.isLoading;

    return (
      <div>
        {renderFiles()}
        {!hideInput && (
          <div className={styles["chat-input"]}>
            {areAttachmentsEnabled && renderAddFileIcon()}
            {renderTextArea()}
            {renderEmojiButton()}
            {isMicrophoneEnabled && renderMicrophoneButton()}
            {renderSendButton()}
          </div>
        )}
        {isEmojiPickerOpen && renderEmojiPicker()}
      </div>
    );
  };

  return render();
});
